import { Component } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { ApiService } from 'src/app/Services/api.service';

interface DecathlonImportResponse {
  Rows: DecathlonPermitRowResponse[];
  ProcessedCount: number;
  SuccessCount: number;
  FailureCount: number;
  SkippedCount: number;
  CreatedCount: number;
  UpdatedCount: number;
  EndedCount: number;
  UserCreatedCount: number;
}

interface DecathlonPermitRowResponse {
  Success: boolean;
  Skipped: boolean;
  ErrorCode?: string;
  ErrorMessage?: string;
  Action?: string;
  RowNumber?: number;
  SourceReference?: string;
  BadgeId?: string;
  UserCreated: boolean;
  GateName?: string;
  ParkingLotName?: string;
  ParkingPermitTypeName?: string;
  ParkingPermitId?: number;
  PermitEndDate?: string;
}

@Component({
  selector: 'app-decathlon-import',
  templateUrl: './decathlon-import.component.html',
  styleUrls: ['./decathlon-import.component.scss'],
  standalone: false
})
export class DecathlonImportComponent {
  public selectedFile?: File;
  public removeMissingPermits = false;
  public processing = false;
  public result?: DecathlonImportResponse;

  constructor(private apiService: ApiService, private toastr: ToastrService) { }

  public onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectedFile = input.files && input.files.length > 0 ? input.files[0] : undefined;
    this.result = undefined;
  }

  public async upload(): Promise<void> {
    if (!this.selectedFile) {
      this.toastr.error('Select a Decathlon CSV file first.');
      return;
    }

    const lowerName = this.selectedFile.name.toLowerCase();
    if (!lowerName.endsWith('.csv') && !lowerName.endsWith('.xlsx')) {
      this.toastr.error('Only CSV and XLSX files are supported.');
      return;
    }

    this.processing = true;
    try {
      const bytesBase64 = await this.readFileAsBase64(this.selectedFile);
      this.result = await this.apiService.Post<DecathlonImportResponse>('integrations/decathlon/permits/import', {
        FileName: this.selectedFile.name,
        ContentType: this.selectedFile.type || (lowerName.endsWith('.xlsx') ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'text/csv'),
        Bytes: bytesBase64,
        RemoveMissingPermits: this.removeMissingPermits
      });
      this.toastr.success('Decathlon import completed.');
    } finally {
      this.processing = false;
    }
  }

  public rowStatus(row: DecathlonPermitRowResponse): string {
    if (row.Skipped) return 'Skipped';
    if (row.Success) return 'Success';
    return 'Failed';
  }

  private readFileAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(reader.error);
      reader.onload = () => {
        const buffer = reader.result as ArrayBuffer;
        resolve(this.arrayBufferToBase64(buffer));
      };
      reader.readAsArrayBuffer(file);
    });
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
    }

    return btoa(binary);
  }
}
